import { useEffect, useRef, useState, KeyboardEvent as ReactKeyboardEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronDown, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import accionLogo from "@/assets/accion-logo.webp";
import SkipLink from "@/components/SkipLink";

const CALENDLY_URL = "https://calendly.com/cjudd-acciongroup/30min";

type NavSection = { label: string; hash?: string; href?: string; route?: string };

type NavItem = {
  label: string;
  to: string;
  sections: NavSection[];
};

const navLinks: NavItem[] = [
  {
    label: "Home",
    to: "/",
    sections: [
      { label: "Services", hash: "services" },
      { label: "Who We Work With", hash: "audiences" },
      { label: "How We're Different", hash: "differentiators" },
      { label: "Experience", hash: "experience" },
      { label: "Data Security", hash: "data-security" },
    ],
  },
  {
    label: "Products",
    to: "/products/powerpro",
    sections: [
      { label: "PowerPro™: Utility Energy Procurement", route: "/products/powerpro" },
      { label: "UtilityFiling: Rate Case and IRP Management", route: "/products/utilityfiling" },
      { label: "ReGreen Exchange: Marketplace tech", route: "/products/regreen" },
    ],
  },
  {
    label: "For Developers",
    to: "/for-developers",
    sections: [
      { label: "Live Opportunities", route: "/for-developers" },
      { label: "Register for PowerPro", hash: "live-opportunities" },
      { label: "Sign Up for Notifications", hash: "notifications" },
    ],
  },
  {
    label: "About",
    to: "/about",
    sections: [
      { label: "Our Story", route: "/about" },
      { label: "Why Accion", hash: "pillars" },
      { label: "Our People", hash: "people" },
      { label: "Contact", hash: "contact" },
    ],
  },
  {
    label: "Careers",
    to: "/careers",
    sections: [
      { label: "Join Accion", hash: "hero" },
      { label: "Open Roles", href: "https://www.linkedin.com/company/accion-group/" },
      { label: "Compensation Philosophy", hash: "philosophy" },
    ],
  },
];

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const desktopNavRef = useRef<HTMLDivElement>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const menuItemRefs = useRef<Record<string, Array<HTMLElement | null>>>({});

  // Scroll to hash after route change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location]);

  // Close on click outside
  useEffect(() => {
    if (!openMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (desktopNavRef.current && !desktopNavRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openMenu]);

  // Close on Escape (global)
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (openMenu) {
          const trigger = triggerRefs.current[openMenu];
          setOpenMenu(null);
          trigger?.focus();
        }
        if (mobileOpen) setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [openMenu, mobileOpen]);

  const handleSectionClick = (to: string, hash: string) => {
    setOpenMenu(null);
    setMobileOpen(false);
    if (location.pathname === to) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      navigate(`${to}#${hash}`, { replace: true });
    } else {
      navigate(`${to}#${hash}`);
    }
  };

  const focusMenuItem = (menuLabel: string, index: number) => {
    const items = menuItemRefs.current[menuLabel] ?? [];
    const valid = items.filter(Boolean) as HTMLElement[];
    if (!valid.length) return;
    const i = (index + valid.length) % valid.length;
    valid[i]?.focus();
  };

  const openMenuAndFocus = (label: string, index = 0) => {
    setOpenMenu(label);
    requestAnimationFrame(() => focusMenuItem(label, index));
  };

  const handleTriggerKeyDown = (
    e: ReactKeyboardEvent<HTMLButtonElement>,
    link: NavItem,
    triggerIndex: number,
  ) => {
    const triggerKeys = Object.keys(triggerRefs.current);
    switch (e.key) {
      case "ArrowDown":
      case "Enter":
      case " ":
        e.preventDefault();
        openMenuAndFocus(link.label, 0);
        break;
      case "ArrowUp":
        e.preventDefault();
        openMenuAndFocus(link.label, -1);
        break;
      case "ArrowRight": {
        e.preventDefault();
        const next = navLinks[(triggerIndex + 1) % navLinks.length];
        triggerRefs.current[next.label]?.focus();
        if (openMenu) setOpenMenu(next.label);
        break;
      }
      case "ArrowLeft": {
        e.preventDefault();
        const prev = navLinks[(triggerIndex - 1 + navLinks.length) % navLinks.length];
        triggerRefs.current[prev.label]?.focus();
        if (openMenu) setOpenMenu(prev.label);
        break;
      }
    }
    void triggerKeys;
  };

  const handleMenuKeyDown = (
    e: ReactKeyboardEvent<HTMLElement>,
    menuLabel: string,
    itemIndex: number,
  ) => {
    const items = (menuItemRefs.current[menuLabel] ?? []).filter(Boolean) as HTMLElement[];
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusMenuItem(menuLabel, itemIndex + 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        focusMenuItem(menuLabel, itemIndex - 1);
        break;
      case "Home":
        e.preventDefault();
        focusMenuItem(menuLabel, 0);
        break;
      case "End":
        e.preventDefault();
        focusMenuItem(menuLabel, items.length - 1);
        break;
      case "Tab":
        setOpenMenu(null);
        break;
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border/50 bg-background">
      <SkipLink />
      <nav className="container mx-auto flex h-20 items-center justify-between px-4 lg:px-8" aria-label="Primary">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 transition-transform duration-300 ease-out hover:scale-[1.03]" aria-label="Home">
          <img src={accionLogo} alt="Accion Group" width="56" height="56" decoding="async" className="h-14 w-auto" />
        </Link>

        {/* Desktop nav */}
        <div ref={desktopNavRef} className="hidden md:flex items-center gap-2 ml-auto mr-4" role="menubar">
          {navLinks.map((link, triggerIndex) => {
            const isActive = location.pathname === link.to;
            const isOpen = openMenu === link.label;
            const menuId = `nav-menu-${slug(link.label)}`;
            // reset slot on each render so refs stay in order
            menuItemRefs.current[link.label] = [];
            return (
              <div
                key={link.to}
                className="relative"
                onMouseEnter={() => setOpenMenu(link.label)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <button
                  ref={(el) => {
                    triggerRefs.current[link.label] = el;
                  }}
                  onClick={() => {
                    if (location.pathname !== link.to) navigate(link.to);
                    setOpenMenu(isOpen ? null : link.label);
                  }}
                  onKeyDown={(e) => handleTriggerKeyDown(e, link, triggerIndex)}
                  className={`group relative inline-flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded ${
                    isActive ? "text-accent" : "text-muted-foreground"
                  }`}
                  role="menuitem"
                  aria-haspopup="menu"
                  aria-expanded={isOpen}
                  aria-controls={menuId}
                >
                  <span className="relative inline-flex flex-col items-center leading-none">
                    <span className="inline-block">{link.label}</span>
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none absolute left-0 right-0 -bottom-1.5 h-px bg-accent origin-center transition-transform duration-300 ease-out ${
                        isActive || isOpen ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    aria-hidden="true"
                  />
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      id={menuId}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-0 top-full min-w-[220px] rounded-md border border-border/60 bg-card shadow-lg overflow-hidden"
                      role="menu"
                      aria-label={`${link.label} menu`}
                    >
                      {link.sections.map((s, itemIndex) => {
                        const key = s.hash ?? s.href ?? s.route ?? s.label;
                        const commonClass =
                          "block w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-secondary hover:text-accent focus:bg-secondary focus:text-accent focus:outline-none transition-colors";
                        const setRef = (el: HTMLElement | null) => {
                          menuItemRefs.current[link.label][itemIndex] = el;
                        };
                        if (s.href) {
                          return (
                            <a
                              key={key}
                              ref={setRef as (el: HTMLAnchorElement | null) => void}
                              href={s.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setOpenMenu(null)}
                              onKeyDown={(e) => handleMenuKeyDown(e, link.label, itemIndex)}
                              role="menuitem"
                              tabIndex={-1}
                              className={commonClass}
                            >
                              {s.label}
                            </a>
                          );
                        }
                        if (s.route) {
                          return (
                            <Link
                              key={key}
                              ref={setRef as (el: HTMLAnchorElement | null) => void}
                              to={s.route}
                              onClick={() => setOpenMenu(null)}
                              onKeyDown={(e) => handleMenuKeyDown(e, link.label, itemIndex)}
                              role="menuitem"
                              tabIndex={-1}
                              className={commonClass}
                            >
                              {s.label}
                            </Link>
                          );
                        }
                        return (
                          <button
                            key={key}
                            ref={setRef as (el: HTMLButtonElement | null) => void}
                            onClick={() => handleSectionClick(link.to, s.hash!)}
                            onKeyDown={(e) => handleMenuKeyDown(e, link.label, itemIndex)}
                            role="menuitem"
                            tabIndex={-1}
                            className={commonClass}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:block">
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">
            <Link to="/contact">Get In Touch</Link>
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2 text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            id="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden border-t border-border/50 bg-background"
          >
            <div className="container mx-auto flex flex-col gap-1 px-4 py-4">
              {navLinks.map((link) => {
                const expanded = mobileExpanded === link.label;
                const submenuId = `mobile-submenu-${slug(link.label)}`;
                return (
                  <div key={link.to} className="border-b border-border/40 last:border-0">
                    <div className="flex items-center justify-between">
                      <Link
                        to={link.to}
                        onClick={() => {
                          setMobileOpen(false);
                          setMobileExpanded(null);
                        }}
                        className={`flex-1 py-3 text-sm font-medium ${
                          location.pathname === link.to ? "text-accent" : "text-foreground"
                        }`}
                      >
                        {link.label}
                      </Link>
                      <button
                        onClick={() => setMobileExpanded(expanded ? null : link.label)}
                        className="p-3 text-muted-foreground"
                        aria-label={`Toggle ${link.label} sections`}
                        aria-expanded={expanded}
                        aria-controls={submenuId}
                      >
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                    <AnimatePresence>
                      {expanded && (
                        <motion.div
                          id={submenuId}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                          role="menu"
                          aria-label={`${link.label} sections`}
                        >
                          <div className="flex flex-col pl-4 pb-2">
                            {link.sections.map((s) => {
                              const key = s.hash ?? s.href ?? s.route ?? s.label;
                              if (s.href) {
                                return (
                                  <a
                                    key={key}
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => {
                                      setMobileOpen(false);
                                      setMobileExpanded(null);
                                    }}
                                    role="menuitem"
                                    className="text-left py-2 text-sm text-muted-foreground hover:text-accent"
                                  >
                                    {s.label}
                                  </a>
                                );
                              }
                              if (s.route) {
                                return (
                                  <Link
                                    key={key}
                                    to={s.route}
                                    onClick={() => {
                                      setMobileOpen(false);
                                      setMobileExpanded(null);
                                    }}
                                    role="menuitem"
                                    className="text-left py-2 text-sm text-muted-foreground hover:text-accent"
                                  >
                                    {s.label}
                                  </Link>
                                );
                              }
                              return (
                                <button
                                  key={key}
                                  onClick={() => handleSectionClick(link.to, s.hash!)}
                                  role="menuitem"
                                  className="text-left py-2 text-sm text-muted-foreground hover:text-accent"
                                >
                                  {s.label}
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
              <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold w-full mt-3">
                <Link to="/contact" onClick={() => setMobileOpen(false)}>Get In Touch</Link>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
